import {
  collection,
  CollectionReference,
  orderBy,
  query,
} from "@firebase/firestore";
import { keyBy } from "lodash";
import React from "react";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import Header from "../../components/Header";
import { WithUser } from "../../components/WithUser";
import { useMembers } from "../../utils/members";

interface AGMResponse {
  id: string;
  uid: string;
  attending: "self" | "representative" | "not-attending";
  representativeName?: string;
  representativeEmail?: string;
}

function AGMAdmin() {
  const members = useMembers();
  const firestore = useFirestore();

  const responsesCollection = query(
    collection(firestore, "agm-responses") as CollectionReference<AGMResponse>,
    orderBy("updatedAt", "desc"),
  );

  const { data: responses = [] } = useFirestoreCollectionData(
    responsesCollection,
    { idField: "id" },
  );

  const responsesByUid = keyBy(responses, "uid");

  const attending = members.filter(
    (m) => responsesByUid[m.uid]?.attending === "self",
  );
  const representative = members.filter(
    (m) => responsesByUid[m.uid]?.attending === "representative",
  );
  const notAttending = members.filter(
    (m) => responsesByUid[m.uid]?.attending === "not-attending",
  );
  const noResponse = members.filter((m) => !responsesByUid[m.uid]);

  return (
    <div id="wrapper">
      <Header dark hideDashboardLink />
      <div className="container pt-5">
        <h2 className="mb-4">AGM 2026 - Responses</h2>

        <div className="row mb-4">
          <div className="col-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="mb-0">{attending.length}</h3>
                <small className="text-muted">Attending</small>
              </div>
            </div>
          </div>
          <div className="col-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="mb-0">{representative.length}</h3>
                <small className="text-muted">Representative</small>
              </div>
            </div>
          </div>
          <div className="col-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="mb-0">{notAttending.length}</h3>
                <small className="text-muted">Not Attending</small>
              </div>
            </div>
          </div>
          <div className="col-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="mb-0">{noResponse.length}</h3>
                <small className="text-muted">No Response</small>
              </div>
            </div>
          </div>
        </div>

        <h4 className="mt-4">Attending ({attending.length})</h4>
        <table className="table table-striped mb-5">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {attending.map((m: any) => (
              <tr key={m.uid}>
                <td>{m.displayName || "—"}</td>
                <td>{m.email}</td>
              </tr>
            ))}
            {attending.length === 0 && (
              <tr>
                <td colSpan={2} className="text-muted">
                  None
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <h4>Sending Representative ({representative.length})</h4>
        <table className="table table-striped mb-5">
          <thead>
            <tr>
              <th>Member</th>
              <th>Representative Name</th>
              <th>Representative Email</th>
            </tr>
          </thead>
          <tbody>
            {representative.map((m: any) => {
              const r = responsesByUid[m.uid];
              return (
                <tr key={m.uid}>
                  <td>{m.displayName || m.email}</td>
                  <td>{r?.representativeName || "—"}</td>
                  <td>{r?.representativeEmail || "—"}</td>
                </tr>
              );
            })}
            {representative.length === 0 && (
              <tr>
                <td colSpan={3} className="text-muted">
                  None
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <h4>Not Attending ({notAttending.length})</h4>
        <table className="table table-striped mb-5">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {notAttending.map((m: any) => (
              <tr key={m.uid}>
                <td>{m.displayName || "—"}</td>
                <td>{m.email}</td>
              </tr>
            ))}
            {notAttending.length === 0 && (
              <tr>
                <td colSpan={2} className="text-muted">
                  None
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <h4>No Response ({noResponse.length})</h4>
        <table className="table table-striped mb-5">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {noResponse.map((m: any) => (
              <tr key={m.uid}>
                <td>{m.displayName || "—"}</td>
                <td>{m.email}</td>
              </tr>
            ))}
            {noResponse.length === 0 && (
              <tr>
                <td colSpan={2} className="text-muted">
                  None
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default WithUser(AGMAdmin);
